import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import AccessibilityWidget from "@/components/layout/AccessibilityWidget";
import RouteTracker from "@/components/RouteTracker";
import ScrollToTop from "@/components/ScrollToTop";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider } from "@/context/AuthContext";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Rabbi from "@/pages/Rabbi";
import Worship from "@/pages/Worship";
import Education from "@/pages/Education";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Hiring from "@/pages/Hiring";
import Membership from "@/pages/Membership";
import MembershipApply from "@/pages/MembershipApply";
import Donate from "@/pages/Donate";
import DonateSuccess from "@/pages/DonateSuccess";
import Contact from "@/pages/Contact";
import Accessibility from "@/pages/Accessibility";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Gallery from "@/pages/Gallery";
import Bulletin from "@/pages/Bulletin";

import Staff from "@/pages/about/Staff";
import President from "@/pages/about/President";
import Board from "@/pages/about/Board";
import Sisterhood from "@/pages/about/Sisterhood";
import SocialAction from "@/pages/about/SocialAction";
import MensClub from "@/pages/about/MensClub";

import Shabbat from "@/pages/worship/Shabbat";
import Holidays from "@/pages/worship/Holidays";
import Yahrzeit from "@/pages/worship/Yahrzeit";

import AdultEducation from "@/pages/education/Adult";
import YouthEducation from "@/pages/education/Youth";
import PathToJudaism from "@/pages/education/PathToJudaism";

import CalendarWeekly from "@/pages/calendar/Weekly";
import CalendarMonthly from "@/pages/calendar/Monthly";
import HighHolyDays from "@/pages/calendar/HighHolyDays";
import ZoomMeetings from "@/pages/calendar/Zoom";
import FilmSeries from "@/pages/calendar/FilmSeries";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminWeekly from "@/pages/admin/AdminWeekly";
import AdminMembership from "@/pages/admin/AdminMembership";
import AdminHiring from "@/pages/admin/AdminHiring";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminContent from "@/pages/admin/AdminContent";
import AdminNews from "@/pages/admin/AdminNews";
import AdminNavigation from "@/pages/admin/AdminNavigation";
import AdminPages from "@/pages/admin/AdminPages";
import AdminHelp from "@/pages/admin/AdminHelp";

import CustomPageView from "@/pages/CustomPageView";
import News from "@/pages/News";
import NewsPost from "@/pages/NewsPost";
import AnnouncementBanner from "@/components/AnnouncementBanner";

function PublicChrome({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) return children;
  return (
    <>
      <AnnouncementBanner />
      <Header />
      <main className="flex-1" id="main-content" tabIndex={-1}>
        {children}
      </main>
      <Footer />
      <AccessibilityWidget />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <RouteTracker />
          <ScrollToTop />
          <PublicChrome>
            <Routes>
              <Route path="/" element={<Home />} />

              {/* About cluster */}
              <Route path="/about" element={<About />} />
              <Route path="/about/staff" element={<Staff />} />
              <Route path="/about/president" element={<President />} />
              <Route path="/about/board" element={<Board />} />
              <Route path="/about/sisterhood" element={<Sisterhood />} />
              <Route path="/about/mens-club" element={<MensClub />} />
              <Route path="/about/social-action" element={<SocialAction />} />
              {/* /about/gala retired - redirect any old bookmarks to /about */}
              <Route path="/about/gala" element={<Navigate to="/about" replace />} />

              <Route path="/rabbi" element={<Rabbi />} />

              {/* Worship cluster */}
              <Route path="/worship" element={<Worship />} />
              <Route path="/worship/shabbat" element={<Shabbat />} />
              <Route path="/worship/holidays" element={<Holidays />} />
              <Route path="/worship/yahrzeit" element={<Yahrzeit />} />

              {/* Education cluster */}
              <Route path="/education" element={<Education />} />
              <Route path="/education/adult" element={<AdultEducation />} />
              <Route path="/education/youth" element={<YouthEducation />} />
              <Route path="/education/path-to-judaism" element={<PathToJudaism />} />

              {/* Calendar cluster */}
              <Route path="/events" element={<Events />} />
              <Route path="/events/:slug" element={<EventDetail />} />
              <Route path="/hiring" element={<Hiring />} />
              <Route path="/calendar/weekly" element={<CalendarWeekly />} />
              <Route path="/calendar/month" element={<CalendarMonthly />} />
              <Route path="/calendar/high-holy-days" element={<HighHolyDays />} />
              <Route path="/calendar/zoom" element={<ZoomMeetings />} />
              <Route path="/calendar/film-series" element={<FilmSeries />} />

              {/* Other */}
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/bulletin" element={<Bulletin />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/membership/apply" element={<MembershipApply />} />
              <Route path="/donate" element={<Donate />} />
              <Route path="/donate/success" element={<DonateSuccess />} />
              <Route path="/contact" element={<Contact />} />

              {/* Legal */}
              <Route path="/accessibility" element={<Accessibility />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              {/* CMS-authored routes */}
              <Route path="/pages/:slug" element={<CustomPageView />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<NewsPost />} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminEvents />} />
                <Route path="events" element={<AdminEvents />} />
                <Route path="weekly" element={<AdminWeekly />} />
                <Route path="membership" element={<AdminMembership />} />
                <Route path="hiring" element={<AdminHiring />} />
                <Route path="staff" element={<AdminStaff />} />
                <Route path="pages" element={<AdminPages />} />
                <Route path="content" element={<AdminContent />} />
                <Route path="news" element={<AdminNews />} />
                <Route path="announcements" element={<AdminAnnouncements />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="navigation" element={<AdminNavigation />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="help" element={<AdminHelp />} />
              </Route>
            </Routes>
          </PublicChrome>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
